import { Carrot } from "@/components/Carrot";
import { CarrotIcon } from "@/components/CarrotIcon";
import { useContactDialog } from "@/components/ContactDialog";
import { Reveal } from "@/components/Reveal";
import { REGINA_EMAIL, SIGNUP_URL, VADIM_EMAIL } from "@/lib/site";

const FOUNDERS = [
  {
    name: "Regina",
    role: "Barns · onboarding · equine",
    email: REGINA_EMAIL,
  },
  {
    name: "Vadim",
    role: "Product · data · AI",
    email: VADIM_EMAIL,
  },
] as const;

export function CallToAction() {
  const { openContact } = useContactDialog();

  return (
    <section id="contact" className="mx-auto max-w-7xl px-5 pb-12 sm:px-8 sm:pb-14">
      <Reveal className="closing-reveal">
        <div className="closing-shell">
          <div className="grid gap-2 lg:grid-cols-[1.42fr_0.58fr]">
            <article className="closing-primary relative overflow-hidden rounded-[1.9rem] px-7 py-12 text-cream sm:px-12 sm:py-16 lg:px-16 lg:py-20">
              <svg
                aria-hidden
                viewBox="0 0 900 420"
                preserveAspectRatio="none"
                className="pointer-events-none absolute inset-0 size-full text-carrot/35"
              >
                <path
                  d="M-30 365 C 130 310, 190 395, 330 310 S 520 170, 690 220 S 820 110, 950 78"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  pathLength={1}
                  className="closing-thread"
                />
              </svg>
              <Carrot
                aria-hidden
                className="closing-carrot pointer-events-none absolute -bottom-16 -right-9 h-72 w-64 opacity-[0.13] sm:-bottom-20 sm:right-3 sm:h-96 sm:w-80"
              />

              <div className="relative max-w-3xl">
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.24em] text-[#f2a663]">
                  Founding barns · now onboarding
                </p>
                <h2 className="mt-6 max-w-[12ch] text-4xl font-extrabold leading-[0.94] tracking-[-0.045em] text-cream sm:text-6xl lg:text-[4.4rem]">
                  Your barn already works. <span className="text-carrot">Give it a memory.</span>
                </h2>
                <p className="mt-7 max-w-xl text-lg leading-relaxed text-cream/72">
                  Start with the PDFs, sheets, photos and voice notes you already have. We turn that scattered
                  history into a system your whole barn can ask, trust and act on.
                </p>

                <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <a href={SIGNUP_URL} className="action-button closing-cta group">
                    <span className="closing-cta-label">Start with your files</span>
                    <span className="closing-cta-icon" aria-hidden>
                      <CarrotIcon className="h-6 w-5" />
                    </span>
                  </a>
                  <span className="text-sm font-semibold text-cream/68">No migration project required.</span>
                </div>
              </div>
            </article>

            <aside className="closing-founders flex flex-col rounded-[1.9rem] bg-paper px-6 py-10 text-ink sm:px-9 sm:py-12 lg:px-8 lg:py-14">
              <p className="text-[0.66rem] font-bold uppercase tracking-[0.22em] text-[#a84816]">Talk to the founders</p>
              <h3 className="mt-5 text-3xl font-extrabold leading-[1.02] text-field-deep">
                Real people, on the other side.
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-ink/70">
                We answer our own email. Tell us what your barn runs on today and where it starts to break.
              </p>

              <ul className="mt-8 border-t border-ink/10">
                {FOUNDERS.map((founder, index) => (
                  <li key={founder.email} className={index ? "border-t border-ink/10" : ""}>
                    <a href={`mailto:${founder.email}`} className="founder-link block py-5">
                      <span className="flex items-center justify-between gap-4">
                        <strong className="font-display text-xl text-ink">{founder.name}</strong>
                        <span className="founder-link-icon" aria-hidden>
                          <CarrotIcon className="h-5 w-4" />
                        </span>
                      </span>
                      <span className="mt-1 block text-xs font-semibold uppercase tracking-[0.12em] text-ink/65">
                        {founder.role}
                      </span>
                      <span className="mt-2 block text-sm font-semibold text-field-deep">{founder.email}</span>
                    </a>
                  </li>
                ))}
              </ul>

              <button type="button" onClick={openContact} className="closing-contact mt-8 lg:mt-auto">
                Tell us about your barn
                <span className="closing-contact-dot" aria-hidden />
              </button>
            </aside>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
